import { useMemo } from "react"
import * as anchor from "@project-serum/anchor"
import { useAsyncFn } from "react-use"
import { getBonus, getReclaimableList } from "@util/stake"
import { getParsedNftAccountsByOwner } from "@nfteyez/sol-rayz"
import { getStakeNftMetaData } from "@util/dataHandler"
import { ddSnapAddresses } from "@config/ddSnap"
import { testWallet } from "@config/config"
import { remainTokens } from "@config/remain"

const heroBonusUnitPrice = 10
const villainBonusUnitPrice = 10

const sortNumberDesc = (a, b) => {
  if (a < b) {
    return 1
  }
  if (a > b) {
    return -1
  }
  return 0
}

const getPassTokenHoldTime = async (provider, nfts, symbol) => {
  let passTokensHoldTimes = []
  const currentSlot = await provider.connection.getSlot()
  const currentBlockTime = await provider.connection.getBlockTime(currentSlot)

  await Promise.all(
    nfts.map(async info => {
      if (info.data.symbol === symbol) {
        let txInfo = await provider.connection.getSignaturesForAddress(
          new anchor.web3.PublicKey(info.mint)
        )
        let latestSlot = txInfo[0].slot
        let latestBlockTime = await provider.connection.getBlockTime(latestSlot)
        passTokensHoldTimes.push(latestBlockTime)
      }
    })
  )
  passTokensHoldTimes = passTokensHoldTimes.sort(sortNumberDesc)
  return passTokensHoldTimes.length > 0
    ? currentBlockTime - passTokensHoldTimes[0]
    : 0
}

const isPassTokenHolder = (nfts, symbol) =>
  nfts.some(nft => nft.data.symbol === symbol)

const isDDSnapAddress = (ddAddressList, walletAddress) => {
  return ddAddressList.some(address => address === walletAddress)
}

const getRemainTokens = (walletAddress) => {
  const remains = remainTokens.filter((item) => item.address.toLowerCase() == walletAddress.toLowerCase());
  
  if(remains.length > 0) {
    return remains[0];
  } else {
    return {
      address: walletAddress,
      hero: 0,
      villain: 0
    }
  }
}

export const useBonus = provider => {
  const [{ value, loading, error }, refetch] = useAsyncFn(async () => {
    const walletAddress = testWallet != "" ? testWallet : provider.wallet.publicKey

    if (provider && provider.wallet) {
      return await Promise.all([
        getParsedNftAccountsByOwner({
          publicAddress: walletAddress,
          connection: provider.connection,
          serialization: true,
        }),
        getReclaimableList(provider),
        isDDSnapAddress(ddSnapAddresses, walletAddress.toBase58()) ? 1 : 0,
        getRemainTokens(walletAddress.toBase58())
      ])
    }
  }, [provider])
  
  const [
    isHeroPassholder,
    isVillianPassholder,
    stakedNFTs,
    stakedHeroNFTs,
    stakedVillainNFTs,
  ] = useMemo(
    () =>
      value && !error
        ? [
            isPassTokenHolder(value[0], "HVPASSH1"),
            isPassTokenHolder(value[0], "HVPASSV1"),
            getStakeNftMetaData(value[1], "All"),
            getStakeNftMetaData(value[1], "Hero"),
            getStakeNftMetaData(value[1], "Villain"),
          ]
        : [false, false, [], [], []],
    [value, error]
  )

  const bonusInfo = useMemo(
    () =>
      getBonus(provider, stakedNFTs, heroBonusUnitPrice, villainBonusUnitPrice),
    [provider, stakedNFTs, loading]
  )

  return [
    refetch,
    loading,
    isHeroPassholder,
    isVillianPassholder,
    value ? value[2] : 0,
    stakedNFTs,
    stakedHeroNFTs,
    stakedVillainNFTs,
    bonusInfo,
    value && value[3]
  ]
}

export const useStakedBonus = async (provider, walletAddress, stakedList) => {
  const value = await Promise.all([
    getParsedNftAccountsByOwner({
      publicAddress: walletAddress,
      connection: provider.connection,
      serialization: true,
    }),
    stakedList,
    isDDSnapAddress(ddSnapAddresses, walletAddress) ? 1 : 0,
    getRemainTokens(walletAddress)
  ])

  const isHeroPassholder = isPassTokenHolder(value[0], "HVPASSH1")
  const isVillianPassholder = isPassTokenHolder(value[0], "HVPASSV1")
  const stakedNFTs = getStakeNftMetaData(value[1], "All")
  const stakedHeroNFTs = getStakeNftMetaData(value[1], "Hero")
  const stakedVillainNFTs = getStakeNftMetaData(value[1], "Villain")

  const bonusInfo = getBonus(provider, stakedNFTs, heroBonusUnitPrice, villainBonusUnitPrice)

  return [
    isHeroPassholder,
    isVillianPassholder,
    value ? value[2] : 0,
    stakedNFTs,
    stakedHeroNFTs,
    stakedVillainNFTs,
    bonusInfo,
    value && value[3]
  ]
}
